import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getBackendUrl } from '../config';

const AuthContext = createContext(null);

/**
 * Liest den aktuell im LocalStorage gespeicherten Benutzer aus.
 * Prüft zusätzlich, ob das JWT-Token bereits abgelaufen ist.
 * 
 * @returns {object|null} Benutzer- und Token-Daten oder null, falls nicht angemeldet oder abgelaufen
 */
function getStoredUser() {
  try {
    const raw = localStorage.getItem('auth_user');
    if (!raw) return null;
    
    const { user, token, expiry } = JSON.parse(raw);
    
    if (new Date().getTime() > expiry) {
      localStorage.removeItem('auth_user');
      return null;
    }
    
    return { user, token };
  } catch {
    return null;
  }
}

/**
 * AuthProvider stellt den globalen Authentifizierungszustand sowie
 * Funktionen für Login, Registrierung, Logout und Profil-Aktualisierung bereit.
 */
export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(getStoredUser);
  const [hasSynced, setHasSynced] = useState(false);

  /**
   * Loggt einen Benutzer mit E-Mail und Passwort ein.
   * Ermittelt zusätzlich die tatsächliche Rolle des Benutzers (Admin, Dozent oder Student).
   */
  const login = useCallback(async (email, password) => {
    const response = await fetch(`${getBackendUrl()}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Login fehlgeschlagen' }));
      throw new Error(errorData.message || 'Login fehlgeschlagen');
    }

    const data = await response.json();
    
    // Rollennormalisierung von ROLE_... auf einfache Kleinschreibung
    let actualRole = data.role ? data.role.toLowerCase() : '';
    if (actualRole.startsWith('role_')) {
      actualRole = actualRole.substring(5);
    }
    
    // Verifizierung der Tutor-Rolle über die Dozentensuche
    if (actualRole !== 'admin') {
      try {
        const searchRes = await fetch(`${getBackendUrl()}/suche/dozent/${encodeURIComponent(data.email)}`, {
          headers: { 'Authorization': `Bearer ${data.token}` }
        });
        if (searchRes.ok) {
          const foundUsers = await searchRes.json();
          if (foundUsers.some(u => u.email === data.email)) {
            actualRole = 'dozent';
          } else {
            actualRole = 'student';
          }
        }
      } catch (e) {
        console.warn("Rollen-Verifikation per Dozentensuche fehlgeschlagen:", e);
      }
    }
    
    // Token-Ablaufzeitpunkt aus dem JWT-Payload decodieren
    let expiry = new Date().getTime() + 86400000; // Standardmäßig 24 Stunden
    try {
      const base64Url = data.token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => 
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));
      const payload = JSON.parse(jsonPayload);
      if (payload.exp) {
        expiry = payload.exp * 1000;
      }
    } catch (e) {
      console.warn("JWT-Ablaufzeitpunkt konnte nicht decodiert werden:", e);
    }
    
    const authData = {
      token: data.token,
      user: {
        id: data.id,
        name: data.name || data.vollerName,
        email: data.email,
        role: actualRole,
        bio: data.bio || '',
      },
      expiry,
    };

    localStorage.setItem('auth_user', JSON.stringify(authData));
    setAuthState({ user: authData.user, token: authData.token });
    
    return authData;
  }, []);

  /**
   * Registriert einen neuen Benutzer und loggt ihn anschließend automatisch ein.
   * Falls Adressdaten mitgeliefert werden, werden diese ebenfalls hinterlegt.
   */
  const register = useCallback(async (name, email, password, roleName, bio, adresse) => {
    const response = await fetch(`${getBackendUrl()}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, roleName, bio }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Registrierung fehlgeschlagen' }));
      throw new Error(errorData.message || 'Registrierung fehlgeschlagen');
    }
    
    const authData = await login(email, password);

    if (adresse && authData?.token && (adresse.plz || adresse.ort || adresse.strasseHausnummer)) {
      try {
        const addrResponse = await fetch(`${getBackendUrl()}/user/me/adresse`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.token}`
          },
          body: JSON.stringify({
            plz: parseInt(adresse.plz, 10),
            ort: adresse.ort,
            strasseHausnummer: adresse.strasseHausnummer
          })
        });
        if (!addrResponse.ok) {
          console.warn("Adresse konnte nach Registrierung nicht automatisch gesetzt werden.");
        }
      } catch (e) {
        console.warn("Fehler beim automatischen Setzen der Adresse nach Registrierung:", e);
      }
    }
  }, [login]);

  /**
   * Loggt den Benutzer aus und löscht alle Session-Daten.
   */
  const logout = useCallback(() => {
    localStorage.removeItem('auth_user');
    setAuthState(null);
    setHasSynced(false);
  }, []);

  /**
   * Synchronisiert die lokalen Benutzerdaten mit dem Backend beim Mounten der Komponente.
   */
  useEffect(() => {
    if (authState?.token && !hasSynced) {
      fetch(`${getBackendUrl()}/user/me`, {
        headers: { 'Authorization': `Bearer ${authState.token}` }
      })
      .then(res => {
        if (!res.ok) {
          if (res.status === 401) {
            logout();
          }
          throw new Error('Fehler beim Laden des Profils');
        }
        return res.json();
      })
      .then(userData => {
        if (userData) {
          const updatedUser = { 
            ...authState.user, 
            name: userData.vollerName || authState.user.name,
            bio: userData.bio || ''
          };
          setAuthState(prev => ({ ...prev, user: updatedUser }));
          setHasSynced(true);
          
          const raw = localStorage.getItem('auth_user');
          if (raw) {
            const parsed = JSON.parse(raw);
            parsed.user = updatedUser;
            localStorage.setItem('auth_user', JSON.stringify(parsed));
          }
        }
      })
      .catch(err => console.error("Fehler beim Abgleich der Nutzerdaten:", err));
    }
  }, [authState?.token, hasSynced, logout]);

  /**
   * Aktualisiert den Namen und die Biografie des angemeldeten Benutzers.
   */
  const updateProfile = useCallback(async (name, bio) => {
    if (!authState?.token) throw new Error("Nicht authentifiziert");

    const response = await fetch(`${getBackendUrl()}/user/me/profile`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authState.token}`
      },
      body: JSON.stringify({ name, bio }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Profilaktualisierung fehlgeschlagen' }));
      throw new Error(errorData.message || 'Profilaktualisierung fehlgeschlagen');
    }

    const data = await response.json();
    
    const updatedUser = {
      ...authState.user,
      name: data.vollerName,
      bio: data.bio || ''
    };

    setAuthState(prev => ({ ...prev, user: updatedUser }));

    const raw = localStorage.getItem('auth_user');
    if (raw) {
      const parsed = JSON.parse(raw);
      parsed.user = updatedUser;
      localStorage.setItem('auth_user', JSON.stringify(parsed));
    }

    return updatedUser;
  }, [authState]);

  /**
   * Überwacht die Gültigkeit des Tokens in regelmäßigen Intervallen (jede Minute).
   */
  useEffect(() => {
    const interval = setInterval(() => {
      const stored = getStoredUser();
      if (!stored) {
        setAuthState(null);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user: authState?.user, 
      token: authState?.token,
      login, 
      register, 
      logout, 
      updateProfile,
      isAuthenticated: !!authState?.user 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom Hook zur einfachen Verwendung des Authentifizierungs-Contexts.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von <AuthProvider> verwendet werden');
  return ctx;
}
